import { z } from 'zod';
import { appId, appDigest, appText } from './app-v2-primitives.mjs';
import { canonicalJson } from '../data-formats/canonical-json.mjs';
import { TRUSTED_REFRESH_DEFINITIONS } from '../../config/app-trusted-refresh.mjs';

function installedDefinition(target, definitions) {
  return definitions.find(
    (definition) => canonicalJson(definition.mapper) === canonicalJson(target.mapper),
  );
}

/** Installed host configuration only; saved code cannot register a mapper. */
export function createTrustedRefreshTargetSchema(definitions) {
  return z
    .strictObject({
      kind: z.literal('trusted-refresh'),
      sourceId: appId,
      toolId: appId,
      mapper: z.strictObject({ id: appId, version: appText(100), digest: appDigest }),
      collectionId: appId,
    })
    .refine(
      (target) => installedDefinition(target, definitions)?.collectionId === target.collectionId,
      'Trusted refresh requires an installed mapper and its configured collection',
    );
}

export const trustedRefreshTargetSchema = createTrustedRefreshTargetSchema(
  TRUSTED_REFRESH_DEFINITIONS,
);

function configuredFields(collection, definition) {
  return (
    collection?.fields.length === definition.fields.length &&
    definition.fields.every((expected) =>
      collection.fields.some(
        (field) =>
          field.id === expected.id &&
          field.type === expected.type &&
          field.required === expected.required &&
          field.nullable === expected.nullable,
      ),
    )
  );
}

function configuredTool(tool, target, definition) {
  return (
    tool?.sourceId === target.sourceId &&
    tool.toolRef === definition.toolRef &&
    canonicalJson(tool.argumentTemplate) === canonicalJson(definition.arguments)
  );
}

/** Reference validation only; declarations are never source/write authority. */
export function validateTrustedRefreshReferences(
  manifest,
  ctx,
  definitions = TRUSTED_REFRESH_DEFINITIONS,
) {
  manifest.jobs.forEach((job, index) => {
    if (!job.target) return;
    const target = job.target;
    const definition = installedDefinition(target, definitions);
    const source = manifest.sources.find((item) => item.id === target.sourceId);
    const tool = manifest.tools.find((item) => item.id === target.toolId);
    const collection = manifest.collections.find((item) => item.id === target.collectionId);
    if (
      !definition ||
      !source ||
      !configuredTool(tool, target, definition) ||
      !configuredFields(collection, definition)
    )
      ctx.addIssue({
        code: 'custom',
        path: ['jobs', index, 'target'],
        message:
          'Trusted refresh requires its configured source/tool, arguments and exact collection fields',
      });
  });
}
